import { useState } from "react";

import { makePostApiCall, makePutApiCall } from "../../../shared/makeApiCall";
import WeighingUserDetailsForm from "../../../components/forms/setup/weighingUsers/WeighingUserDetailsForm";
import DeleteConfirmationForm from "../../../components/forms/DeleteConfirmationForm";
import {
  updateObjectInArray,
  generateUniqueNumber,
} from "../../../shared/utility";
import ModelErrorComponent from "../../../components/modals/ModelErrorComponent";
import {
  SnackbarData,
  SnackbarType,
} from "../../../components/Snackbar";

export function WeighingUsersViewModel(logout, addSnackbar) {
  const [loading, setLoading] = useState(false);
  const [weighingUsers, setWeighingUsers] = useState({
    data: [],
    total: 0,
  });
  const [slideOver, setSlideOver] = useState({
    childComponent: null,
    open: false,
  });
  const [emptyModal, setEmptyModelOpen] = useState({
    open: false,
    childComponent: null,
  });

  const showErrorModal = (title, subtitle) => {
    setEmptyModelOpen({
      childComponent: (
        <ModelErrorComponent
          setOpen={setEmptyModelOpen}
          subtitle={subtitle}
          title={title}
        />
      ),
      open: true,
    });
  };

  const getWeighingUsers = async function (search, page) {
    setLoading(true);

    const callResult = await makePostApiCall("weighingUsers", {
      page: page || 1,
      search: search ?? "",
    });
    setLoading(false);
    if (callResult.success) {
      setWeighingUsers(callResult.data);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      } else {
        showErrorModal(
          callResult.data?.message || "Error",
          callResult.data?.message
        );
      }
    }
  };

  const createWeighingUser = async function (values) {
    const payload = { name: values.name };
    const callResult = await makePostApiCall("weighingUsers/create", payload);

    if (callResult.success) {
      addSnackbar(
        new SnackbarData(
          generateUniqueNumber(),
          callResult.data.message || "Weighing user added",
          SnackbarType.Success
        )
      );
      setSlideOver({
        childComponent: null,
        open: false,
      });
      setTimeout(function () {
        getWeighingUsers("", 1);
      }, 1000);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      } else {
        showErrorModal(
          callResult.data?.message || "Error",
          callResult.data?.message
        );
      }
    }
  };

  const updateWeighingUser = async function (values, index) {
    const callResult = await makePutApiCall(
      `weighingUsers/${values.id}`,
      { name: values.name }
    );

    if (callResult.success) {
      addSnackbar(
        new SnackbarData(
          generateUniqueNumber(),
          callResult.data.message || "Weighing user updated",
          SnackbarType.Success
        )
      );
      setSlideOver({
        childComponent: null,
        open: false,
      });

      const currentItems = [...weighingUsers.data];
      const payload = {
        index: index,
        item: callResult.data.user,
      };
      const newItems = updateObjectInArray(currentItems, payload);
      setWeighingUsers({ ...weighingUsers, data: newItems });
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      } else {
        showErrorModal(
          callResult.data?.message || "Error",
          callResult.data?.message
        );
      }
    }
  };

  const openAddWeighingUserSlideOver = (isEdit, weighingUser, index) => {
    setSlideOver({
      childComponent: (
        <WeighingUserDetailsForm
          weighingUser={weighingUser}
          isEdit={isEdit}
          setOpen={setSlideOver}
          actionCall={(payload) =>
            isEdit
              ? updateWeighingUser(payload, index)
              : createWeighingUser(payload)
          }
          title={
            isEdit
              ? `Update ${weighingUser.name}`
              : "Add new weighing user"
          }
        />
      ),
      open: true,
    });
  };

  const deleteWeighingUser = async function (id, index) {
    setLoading(true);

    const callResult = await makePostApiCall(`deleteWeighingUser/${id}`);
    setLoading(false);
    if (callResult.success) {
      setEmptyModelOpen({
        open: false,
        childComponent: null,
      });

      const newItems = weighingUsers.data.filter((item) => item.id !== id);
      setWeighingUsers({ ...weighingUsers, data: newItems });

      addSnackbar(
        new SnackbarData(
          generateUniqueNumber(),
          callResult.data.message || "Weighing user deleted",
          SnackbarType.Success
        )
      );
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      } else {
        showErrorModal(
          callResult.data?.message || "Error",
          callResult.data?.message
        );
      }
    }
  };

  const askDeleteWeighingUser = function (weighingUser, index) {
    setEmptyModelOpen({
      childComponent: (
        <DeleteConfirmationForm
          buttonMessage="Delete"
          confirmAction={() => deleteWeighingUser(weighingUser.id, index)}
          message={`Are you sure you want to delete ${weighingUser.name}?`}
          setOpen={setEmptyModelOpen}
          hideReason={true}
        />
      ),
      open: true,
    });
  };

  return {
    openAddWeighingUserSlideOver,
    getWeighingUsers,
    loading,
    weighingUsers,
    setSlideOver,
    slideOver,
    setEmptyModelOpen,
    emptyModal,
    askDeleteWeighingUser,
  };
}
